import { useState, useMemo } from 'react';
import AppLayout from '@/components/layout/AppLayout';
import { Badge } from '@/components/ui/badge';
import { SlidersHorizontal, TrendingUp, AlertTriangle } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Cell } from 'recharts';

const BASE = { pazienti: 4200, costoMedio: 3420, budget: 14364, copertura: 8.4, tempoAttivazione: 38 };

function ComparisonTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white dark:bg-slate-900 p-3 rounded-lg shadow-xl border border-slate-200 dark:border-slate-700 text-xs">
      <p className="font-bold text-slate-800 dark:text-white mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-slate-500">
          <span className="inline-block w-2 h-2 rounded-full mr-1.5" style={{ backgroundColor: p.fill || p.color }} />
          {p.name}: <strong>{typeof p.value === 'number' ? p.value.toLocaleString() : p.value}</strong>
        </p>
      ))}
      {payload.length === 2 && (
        <p className="text-slate-400 text-[10px] mt-1 pt-1 border-t border-slate-100 dark:border-slate-800">
          Delta: {payload[1].value >= payload[0].value ? '+' : ''}{((payload[1].value - payload[0].value) / payload[0].value * 100).toFixed(1)}%
        </p>
      )}
    </div>
  );
}

export default function ASLSimulazione() {
  const [volumeInc, setVolumeInc] = useState(0);
  const [tariffaInc, setTariffaInc] = useState(0);

  const scenario = useMemo(() => {
    const pz = Math.round(BASE.pazienti * (1 + volumeInc / 100));
    const costo = Math.round(BASE.costoMedio * (1 + tariffaInc / 100));
    const budget = Math.round((pz * costo) / 1000);
    const copertura = +(BASE.copertura * (1 + volumeInc / 100)).toFixed(1);
    return { pazienti: pz, costoMedio: costo, budget, copertura, tempoAttivazione: volumeInc > 20 ? 52 : volumeInc > 10 ? 45 : BASE.tempoAttivazione };
  }, [volumeInc, tariffaInc]);

  const comparison = [
    { metrica: 'Pazienti ADI', base: BASE.pazienti, simulato: scenario.pazienti },
    { metrica: 'Costo Medio (€)', base: BASE.costoMedio, simulato: scenario.costoMedio },
    { metrica: 'Budget Totale (K€)', base: BASE.budget, simulato: scenario.budget },
    { metrica: 'Copertura PNRR (%)', base: BASE.copertura, simulato: scenario.copertura },
    { metrica: 'Tempo Attivaz. (h)', base: BASE.tempoAttivazione, simulato: scenario.tempoAttivazione },
  ];

  const budgetExceeded = scenario.budget > BASE.budget * 1.15;

  return (
    <AppLayout role="asl">
      <div className="p-4 md:p-6 max-w-[1600px] mx-auto space-y-6" data-testid="asl-simulazione-page">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-white">Simulazione What-If</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Analisi predittiva — Impatto su budget e risorse al variare dei parametri</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

          {/* Simulation Panel */}
          <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm p-6" data-testid="simulation-panel">
            <div className="flex items-center gap-2 mb-6">
              <SlidersHorizontal className="w-4 h-4 text-violet-500" />
              <h2 className="text-sm font-bold text-slate-800 dark:text-white">Parametri di Simulazione</h2>
            </div>

            {/* Slider 1 */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">Aumento Volumi Pazienti Over-65</label>
                <Badge className={`border-0 text-[10px] font-bold ${volumeInc === 0 ? 'bg-slate-100 dark:bg-slate-800 text-slate-500' : 'bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-400'}`}>+{volumeInc}%</Badge>
              </div>
              <input type="range" min={0} max={50} step={5} value={volumeInc} onChange={e => setVolumeInc(Number(e.target.value))} className="w-full h-2 bg-slate-200 dark:bg-slate-700 rounded-full appearance-none cursor-pointer accent-violet-600" data-testid="slider-volume" />
              <div className="flex justify-between text-[9px] text-slate-400 mt-1"><span>0%</span><span>+25%</span><span>+50%</span></div>
            </div>

            {/* Slider 2 */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">Adeguamento Tariffe Prestazioni</label>
                <Badge className={`border-0 text-[10px] font-bold ${tariffaInc === 0 ? 'bg-slate-100 dark:bg-slate-800 text-slate-500' : 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'}`}>+{tariffaInc}%</Badge>
              </div>
              <input type="range" min={0} max={30} step={5} value={tariffaInc} onChange={e => setTariffaInc(Number(e.target.value))} className="w-full h-2 bg-slate-200 dark:bg-slate-700 rounded-full appearance-none cursor-pointer accent-amber-500" data-testid="slider-tariffa" />
              <div className="flex justify-between text-[9px] text-slate-400 mt-1"><span>0%</span><span>+15%</span><span>+30%</span></div>
            </div>

            {/* Delta Summary */}
            <div className={`rounded-xl p-4 border ${budgetExceeded ? 'bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800/40' : 'bg-emerald-50 dark:bg-emerald-950/20 border-emerald-200 dark:border-emerald-800/40'}`} data-testid="delta-summary">
              <div className="flex items-center gap-2 mb-1">
                {budgetExceeded && <AlertTriangle className="w-3.5 h-3.5 text-red-500" />}
                <p className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Nuovo Fabbisogno Finanziario</p>
              </div>
              <p className={`text-2xl font-bold ${budgetExceeded ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'}`}>€ {scenario.budget.toLocaleString()}K</p>
              <p className="text-[10px] text-slate-500 mt-1">Delta vs attuale: <strong>{scenario.budget > BASE.budget ? '+' : ''}{((scenario.budget - BASE.budget) / BASE.budget * 100).toFixed(1)}%</strong> (€ {(scenario.budget - BASE.budget).toLocaleString()}K)</p>
              {scenario.tempoAttivazione > BASE.tempoAttivazione && (
                <p className="text-[10px] text-red-500 dark:text-red-400 mt-1 font-medium">Attenzione: Tempo medio attivazione supera SLA ({scenario.tempoAttivazione}h vs {BASE.tempoAttivazione}h)</p>
              )}
            </div>
          </div>

          {/* Comparison View */}
          <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm p-6" data-testid="comparison-view">
            <h2 className="text-sm font-bold text-slate-800 dark:text-white mb-4">Confronto: Base vs Simulato</h2>
            <div className="h-64 mb-6">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={comparison} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border, #e2e8f0)" />
                  <XAxis dataKey="metrica" tick={{ fontSize: 9, fill: '#94a3b8' }} interval={0} angle={-15} textAnchor="end" height={50} />
                  <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} />
                  <Tooltip content={<ComparisonTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 10 }} />
                  <Bar dataKey="base" name="Scenario Base" fill="#94a3b8" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="simulato" name="Scenario Simulato" radius={[4, 4, 0, 0]}>
                    {comparison.map((entry, index) => (
                      <Cell
                        key={`sim-${index}`}
                        fill={entry.simulato > entry.base * 1.1 ? (entry.metrica.includes('Tempo') ? '#ef4444' : '#8b5cf6') : '#8b5cf6'}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Side-by-side table */}
            <div className="rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
              <div className="grid grid-cols-4 text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider bg-slate-50 dark:bg-slate-800 px-4 py-2">
                <span>Metrica</span><span className="text-center">Base</span><span className="text-center">Simulato</span><span className="text-center">Delta</span>
              </div>
              {comparison.map(c => {
                const delta = c.simulato - c.base;
                const pctDelta = c.base !== 0 ? ((delta / c.base) * 100).toFixed(1) : '0';
                const isNeg = c.metrica.includes('Tempo') ? delta > 0 : delta < 0;
                return (
                  <div key={c.metrica} className="grid grid-cols-4 px-4 py-2.5 border-t border-slate-100 dark:border-slate-800/60 text-xs">
                    <span className="text-slate-700 dark:text-slate-200 font-medium">{c.metrica}</span>
                    <span className="text-center text-slate-500">{typeof c.base === 'number' ? c.base.toLocaleString() : c.base}</span>
                    <span className={`text-center font-bold ${isNeg ? 'text-red-600 dark:text-red-400' : delta === 0 ? 'text-slate-500' : 'text-emerald-600 dark:text-emerald-400'}`}>{typeof c.simulato === 'number' ? c.simulato.toLocaleString() : c.simulato}</span>
                    <span className={`text-center text-[10px] font-medium ${isNeg ? 'text-red-500' : delta === 0 ? 'text-slate-400' : 'text-emerald-500'}`}>{delta >= 0 ? '+' : ''}{pctDelta}%</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
